/**
 * DebugPanel — Panneau de debug flottant pour @ollaid/native-sso
 * Affiche l'historique des appels API en temps réel (style terminal)
 * N'apparaît que quand debug=true
 * @version 2.7.9
 */

import { useState, useEffect, useCallback } from 'react';
import { subscribeToLogs, clearApiLogs, exportLogsAsText, getApiLogs, type ApiCallLog } from '../services/debugLogger';

export type DebugOnboardingPreset = 'current' | 'photo' | 'phone' | 'email' | 'all';

interface DebugPanelProps {
  saasApiUrl: string;
  iamApiUrl: string;
  onOpenLogin?: () => void;
  onOpenSignup?: () => void;
  onOpenOnboarding?: (preset: DebugOnboardingPreset) => void;
  onOpenPassword?: () => void;
  onResetProfilePrompt?: () => void;
}

export function DebugPanel({ saasApiUrl, iamApiUrl, onOpenLogin, onOpenSignup, onOpenOnboarding, onOpenPassword, onResetProfilePrompt }: DebugPanelProps) {
  const [logs, setLogs] = useState<ApiCallLog[]>(() => getApiLogs());
  const [expanded, setExpanded] = useState(true);
  const [selectedLog, setSelectedLog] = useState<string | null>(null);

  useEffect(() => {
    setLogs(getApiLogs());
    return subscribeToLogs(setLogs);
  }, []);

  const handleCopy = useCallback(() => {
    const text = exportLogsAsText();
    navigator.clipboard?.writeText(text).catch(() => {});
  }, []);

  const handleClear = useCallback(() => {
    clearApiLogs();
    setSelectedLog(null);
  }, []);

  const statusColor = (status: number | null) => {
    if (status === null) return '#f59e0b';
    if (status >= 200 && status < 300) return '#22c55e';
    return '#ef4444';
  };

  const getResponseOutcome = (log: ApiCallLog): { isError: boolean; summary: string | null } => {
    const body = log.responseBody;
    if (!body || typeof body !== 'object') {
      return { isError: log.responseStatus !== null && log.responseStatus >= 400, summary: null };
    }

    const payload = body as Record<string, unknown>;
    const parts: string[] = [];
    const isError = (typeof payload.success === 'boolean' && payload.success === false)
      || (typeof log.responseStatus === 'number' && log.responseStatus >= 400);

    if (typeof payload.status === 'string') parts.push(payload.status);
    if (payload.needs_access === true) parts.push('needs_access');
    if (payload.requires_2fa === true) parts.push('2fa');
    if (typeof payload.error_type === 'string') parts.push(`error:${payload.error_type}`);
    if (typeof payload.callback_token === 'string') parts.push('callback_token');
    if (typeof payload.process_token === 'string') parts.push('process_token');
    if (typeof payload.otp_method === 'string') parts.push(`otp:${payload.otp_method}`);
    if (typeof payload.cooldown_remaining === 'number') parts.push(`cooldown:${payload.cooldown_remaining}s`);

    return { isError, summary: parts.length ? parts.join(' · ') : null };
  };

  const statusEmoji = (status: number | null) => {
    if (status === null) return '⏳';
    if (status >= 200 && status < 300) return '✅';
    return '❌';
  };

  const selected = selectedLog ? logs.find(l => l.id === selectedLog) : null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 99999,
      fontFamily: 'monospace',
      fontSize: '12px',
    }}>
      {/* Header bar */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          background: '#1a1a2e',
          color: '#e2e8f0',
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderTop: '2px solid #6366f1',
        }}
      >
        <span>🔐 Debug SSO — {logs.length} appel{logs.length > 1 ? 's' : ''}</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {expanded && (
            <>
              <button onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}>
                📋 Copier
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleClear(); }}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}>
                🗑️ Effacer
              </button>
            </>
          )}
          <span>{expanded ? '▼' : '▲'}</span>
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div style={{
          background: '#0f0f23',
          color: '#e2e8f0',
          maxHeight: '300px',
          overflowY: 'auto',
        }}>
          {/* Config bar */}
          <div style={{ padding: '4px 12px', background: '#1e1e3f', fontSize: '10px', color: '#94a3b8', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span>SaaS: <span style={{ color: '#6366f1' }}>{saasApiUrl}</span></span>
            <span>IAM: <span style={{ color: '#6366f1' }}>{iamApiUrl}</span></span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenLogin?.(); }}
                disabled={!onOpenLogin}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: onOpenLogin ? 'pointer' : 'not-allowed', fontSize: '11px', opacity: onOpenLogin ? 1 : 0.5 }}
              >
                Connexion
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenSignup?.(); }}
                disabled={!onOpenSignup}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: onOpenSignup ? 'pointer' : 'not-allowed', fontSize: '11px', opacity: onOpenSignup ? 1 : 0.5 }}
              >
                Inscription
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenOnboarding?.('current'); }}
                disabled={!onOpenOnboarding}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: onOpenOnboarding ? 'pointer' : 'not-allowed', fontSize: '11px', opacity: onOpenOnboarding ? 1 : 0.5 }}
              >
                Infos profile
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenPassword?.(); }}
                disabled={!onOpenPassword}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: onOpenPassword ? 'pointer' : 'not-allowed', fontSize: '11px', opacity: onOpenPassword ? 1 : 0.5 }}
              >
                Mot de passe
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onResetProfilePrompt?.(); }}
                disabled={!onResetProfilePrompt}
                style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: onResetProfilePrompt ? 'pointer' : 'not-allowed', fontSize: '11px', opacity: onResetProfilePrompt ? 1 : 0.5 }}
              >
                Reset profil
              </button>
            </div>
          </div>

          {logs.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
              Aucun appel API. Tentez une connexion pour voir les logs.
            </div>
          ) : (
            <div>
              {logs.map(log => (
                <div key={log.id}>
                  {(() => {
                    const outcome = getResponseOutcome(log);
                    const displayStatus = outcome.isError ? '❌' : statusEmoji(log.responseStatus);
                    const displayColor = outcome.isError ? '#ef4444' : statusColor(log.responseStatus);
                    return (
                      <>
                  <div
                    onClick={() => setSelectedLog(selectedLog === log.id ? null : log.id)}
                    style={{
                      padding: '4px 12px',
                      borderBottom: '1px solid #1e293b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: selectedLog === log.id ? '#1e293b' : 'transparent',
                    }}
                  >
                    <span>{displayStatus}</span>
                    <span style={{
                      background: log.method === 'GET' ? '#22c55e' : '#3b82f6',
                      color: '#fff',
                      padding: '0 4px',
                      borderRadius: '2px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                    }}>{log.method}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.endpoint}
                    </span>
                    <span style={{ color: displayColor, minWidth: '30px' }}>
                      {log.responseStatus ?? '...'}
                    </span>
                    <span style={{ color: '#64748b', minWidth: '50px', textAlign: 'right' }}>
                      {log.duration ? `${log.duration}ms` : '...'}
                    </span>
                  </div>

                  {outcome.summary && (
                    <div style={{
                      padding: '0 12px 6px 32px',
                      color: outcome.isError ? '#fca5a5' : '#94a3b8',
                      fontSize: '10px',
                      borderBottom: '1px solid #1e293b',
                    }}>
                      {outcome.summary}
                    </div>
                  )}

                  {/* Detail view */}
                  {selectedLog === log.id && selected && (
                    <div style={{ padding: '8px 12px 8px 32px', background: '#111827', borderBottom: '1px solid #1e293b' }}>
                      <div style={{ color: '#94a3b8', marginBottom: '4px' }}>
                        ⏰ {new Date(log.timestamp).toLocaleTimeString()}
                      </div>
                      {log.requestBody ? (
                        <div style={{ marginBottom: '4px' }}>
                          <div style={{ color: '#6366f1', fontSize: '10px' }}>REQUEST:</div>
                          <pre style={{ color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, maxHeight: '80px', overflow: 'auto' }}>
                            {JSON.stringify(log.requestBody, null, 2)}
                          </pre>
                        </div>
                      ) : null}
                      {log.responseBody ? (
                        <div>
                          <div style={{ color: '#6366f1', fontSize: '10px' }}>RESPONSE:</div>
                          <pre style={{ color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                            {JSON.stringify(log.responseBody, null, 2)}
                          </pre>
                        </div>
                      ) : null}
                      {log.error && (
                        <div style={{ color: '#ef4444', marginTop: '4px' }}>⚠️ {log.error}</div>
                      )}
                    </div>
                  )}
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DebugPanel;
